import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, AlertTriangle } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          setStatus('paid');
          return;
        }
        if (['failed', 'expired'].includes(res.data.payment_status)) {
          setStatus('error');
          return;
        }
      } catch (e) {
        // keep polling
      }
      attempts.current += 1;
      if (attempts.current >= 10) {
        setStatus('timeout');
      } else {
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <div className="flex-1 flex items-center justify-center pt-32 pb-20 px-4">
        <div className="max-w-lg w-full text-center bg-gray-50 rounded-lg border border-gray-200 p-10 shadow-lg" data-testid="payment-success-card">
          {status === 'checking' && (
            <>
              <Loader2 className="mx-auto mb-6 animate-spin text-[#1e3a5f]" size={56} />
              <h1 className="text-2xl font-bold text-[#1e3a5f] mb-3">Confirming your payment...</h1>
              <p className="text-gray-600">Please wait a moment while we verify your purchase.</p>
            </>
          )}
          {status === 'paid' && (
            <>
              <CheckCircle className="mx-auto mb-6 text-green-600" size={56} />
              <h1 className="text-2xl font-bold text-[#1e3a5f] mb-3" data-testid="payment-confirmed-heading">Payment Successful!</h1>
              <p className="text-gray-600 mb-6">
                Thank you for your purchase. We'll be in touch shortly with your course access details.
              </p>
              <Button asChild className="bg-[#dc2626] hover:bg-[#b91c1c] text-white font-semibold" data-testid="back-to-courses-btn">
                <Link to="/online-courses">Back to Courses</Link>
              </Button>
            </>
          )}
          {(status === 'error' || status === 'timeout') && (
            <>
              <AlertTriangle className="mx-auto mb-6 text-amber-500" size={56} />
              <h1 className="text-2xl font-bold text-[#1e3a5f] mb-3">
                {status === 'timeout' ? "We're still confirming your payment" : 'Something went wrong'}
              </h1>
              <p className="text-gray-600 mb-6">
                {status === 'timeout'
                  ? "Your payment may still be processing. If you were charged, you'll receive confirmation shortly. Please contact us on 0401 650 479 if you have any concerns."
                  : 'We could not confirm your payment. Please contact us on 0401 650 479 or try again.'}
              </p>
              <Button asChild variant="outline" className="border-[#1e3a5f] text-[#1e3a5f] font-semibold">
                <Link to="/online-courses">Back to Courses</Link>
              </Button>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
